import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  createRef,
} from "react";
import DOMPurify from "dompurify";
import { styled } from "@linaria/react";
import Button from "../../Components/Button";
import ErrorModal from "../../Components/Modals/ErrorModal";
import { calculateSpacer } from "@utils/spaceCalculation";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { updateUWQCompleted } from "@redux/slices/SHIELD_STARTER/step4Slice";
import { SCREEN_SIZE } from "@static/screen-size";
import { trace } from "console";
import { trackOnLoad } from "@pages/ShieldStarter/Helpers/dataAnalyticsHelpers";

const SectionContainer = styled.div<{ margin?: string; width?: string }>`
  background-color: #ffffff;
  border-radius: 24px;
  opacity: 1;
  -webkit-transition: opacity 0.5s ease-in;
  -moz-transition: opacity 0.5s ease-in;
  -o-transition: opacity 0.5s ease-in;
  -ms-transition: opacity 0.5s ease-in;
  transition: opacity 0.5s ease-in;
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  transition: all 0.2s ease;
  /* width: ${(props) => (props.width ? props.width : "792px")}; */
  margin: 36px 0 0;
  padding: 24px 16px;
  scroll-margin-top: 64px;

  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    scroll-margin-top: 260px;
    padding: 24px;
  }

  @media (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    /* margin-top: 64px; */
    scroll-margin-top: 168px;
  }
`;

interface TextProps {
  color?: string;
  fSize?: string;
  weight?: number;
  mg?: string;
  uLine?: boolean;
}

const Text = styled.p<TextProps>`
  color: ${(props) => (props.color ? props.color : "#434343")};
  font-weight: ${(props) => (props.weight ? props.weight : 600)};
  font-size: ${(props) => (props.fSize ? props.fSize : "12px")};
  margin: ${(props) => (props.mg ? props.mg : "0")};
  font-family: 'Open Sans';

  text-underline-offset: 5px;
  text-decoration-style: dotted;
  text-decoration-line: ${(props) => (props.uLine ? "underline" : "unset")};

  /* display: inline-flex; */
  /* display: -webkit-inline-box; */
  display: inline-block;
  vertical-align: top;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  flex-direction: column;
  align-items: center;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
    max-width: 660px;
    margin: auto;
  }
`;

interface ListProps {
  nColumn?: number;
}

const WrapList = styled.ul<ListProps>`
  column-count: 1;
  column-gap: 80px;
  color: #434343;
  padding-left: 16px;
  width: 100%;

  @media (min-width: ${SCREEN_SIZE.tabletsUp}px) {
    column-count: ${(props) =>
      props.nColumn && props.nColumn > 1 ? props.nColumn : 1};
  }

  @media (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    padding-left: 34px;
    width: auto;
  }

  li {
    margin-bottom: 30px;
    /* display: inline-flex ; */
    width:100%;
    /* padding-left: 16px; */
    position: relative;
    /* &:before {
      content:"• ";
      position: absolute;
      left: 0;
      font-size: 30px;
      line-height: 0.6;
    } */
    
  } 
`;

export function UwqChildList(props: any) {
  const { questionPoints, nColumn, screenSize } = props;
  const TEXT_FONT_SIZE = screenSize >= SCREEN_SIZE.mobilesUp ? "16px" : "14px";
  return (
    <WrapList nColumn={nColumn}>
      {questionPoints.map((point: any, i: number) => (
        <li key={"q" + i}>
          {
            <Text
              fSize="14px"
              weight={400}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(point.QuestionPoint) }}
            />
          }
        </li>
      ))}
    </WrapList>
  );
}

function UwqList(props: any) {
  const history = useHistory();
  const dispatch = useAvivaDispatch();
  const { questionsList, screenSize } = props;
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [answerNo, setAnswerNo] = useState<string[]>([
    questionsList[0]?.questionId?.toString(),
  ]);
  const SpacerRef = useRef<null | HTMLDivElement>(null);
  const elementsRef = useRef(questionsList.map(() => createRef()));
  const isCompleted = useAvivaSelector(
    (state) => state.shieldStarterStep4.isCompleted
  );

  const onNoClick = (txt: string) => {
    const newAnser = [...answerNo, txt];
    setAnswerNo([...newAnser]);
  };

  const completeSteps = useCallback(() => {
    dispatch(updateUWQCompleted(true));
    history.push(ROUTE_PATH.SHIELD_STARTER_DECLARATION);
  }, []);

  const answerYes = useCallback(() => {
    setOpenPopup(true);
    isCompleted && dispatch(updateUWQCompleted(false));
  }, []);

  useEffect(() => {
    setTimeout(() => {
      elementsRef.current.map((ref: any) => {
        if (ref.current) {
          calculateSpacer(ref, SpacerRef, false);
          ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }, 100);
    const currentQuestionIndex = Math.max(answerNo.length-1,0);
    const questionId = questionsList[currentQuestionIndex]?.questionId
    if(questionId){
      trackOnLoad(`sss_underwriting_question_id_${questionId}_form`)
    }
  }, [answerNo]);

  const TITLE_MG_BOTTOM = screenSize >= SCREEN_SIZE.mobilesUp ? "64px" : "24px";
  const TITLE_FONT_SIZE = screenSize >= SCREEN_SIZE.mobilesUp ? "28px" : "18px";
  const BUTTON_MG =
    screenSize >= SCREEN_SIZE.mobilesUp ? "64px 12px 0 12px" : "24px 0 0";
  return (
    <>
      {questionsList.map((question: any, i: number) => {
        const qId = question?.questionId?.toString();
        const qNextId =
          i < questionsList.length - 1
            ? questionsList[i + 1].questionId?.toString()
            : 0;
        const isSelected = isCompleted ? true : answerNo.includes(qNextId);
        if (answerNo.includes(qId) || isCompleted) {
          return (
            <SectionContainer key={i} ref={elementsRef.current[i]}>
              <Center>
                <Text
                  fSize={TITLE_FONT_SIZE}
                  style={{ textAlign: "center", marginBottom: TITLE_MG_BOTTOM }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.questionDesc) }}
                />
              </Center>
              <UwqChildList
                questionPoints={question?.questionPoints}
                nColumn={question?.numOfColumn}
                screenSize={screenSize}
              />
              <Center>
                <Button
                  label="Yes, at least one"
                  bgColor="#FAFAFA"
                  borderColor="rgba(111,111,111,0.2) transparent transparent"
                  textColor="#8A8988"
                  hoverBgColor="#fff"
                  hoverTextColor="#783780"
                  variant="default"
                  margin={BUTTON_MG}
                  width="281px"
                  onClick={answerYes}
                />
                <Button
                  label="No, none of the above"
                  bgColor={isSelected ? "#883E89" : "#FAFAFA"}
                  borderColor="rgba(111,111,111,0.2) transparent transparent"
                  textColor={isSelected ? "#fff" : "#8A8988"}
                  hoverBgColor={isSelected ? "#883E89" : "#fff"}
                  hoverTextColor={isSelected ? "#fff" : "#783780"}
                  variant="default"
                  margin={BUTTON_MG}
                  width="281px"
                  onClick={() => {
                    if (qNextId === 0) {
                      completeSteps();
                      return;
                    }
                    if (!isSelected) {
                      onNoClick(qNextId);
                    }
                  }}
                />
              </Center>
            </SectionContainer>
          );
        }
      })}
      <div ref={SpacerRef}></div>
      {openPopup && (
        <ErrorModal
          title="You may need some assistance"
          error="It looks like you might need assistance from a Financial Adviser Representative
          to find a more suitable plan for your needs."
          onCloseFn={() => setOpenPopup(false)}
        />
      )}
    </>
  );
}
export default UwqList;
